import {
  AccountBalanceOutlined,
  ApartmentOutlined,
  CheckCircleOutlined,
  KeyboardDoubleArrowRightOutlined,
  LocalAtmOutlined,
  ShowChartOutlined,
  SyncAltOutlined,
} from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';
import { AccountingPeriodChecklist } from 'further-types/lp/accounting-period';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';

type ChecklistItemDef = {
  key: keyof AccountingPeriodChecklist;
  label: React.ReactNode;
  subLabelTemplate: (count: string) => string;
  icon: React.ReactNode;
  formatValue?: (value: number, currency?: string) => string;
};

export const requiredItems: Array<ChecklistItemDef> = [
  {
    key: 'bankTransactionsReconciliation',
    label: 'Confirm bank transactions awaiting reconciliation',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <AccountBalanceOutlined fontSize="small" />,
  },
  {
    key: 'bankBalancesStatement',
    label: 'Check end of quarter bank balances against statement balances',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('account', Number(count))}`,
    icon: <AccountBalanceOutlined fontSize="small" />,
  },
  {
    key: 'incomeExpensesInvestmentsAndRealisations',
    label:
      'Confirm all incomes, expenses, investments and realisations entered',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('action', Number(count))}`,
    icon: <SyncAltOutlined fontSize="small" />,
  },
  {
    key: 'accrualsPayablesReceivables',
    label: 'Approve all outstanding accruals, payables and receivables',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <SyncAltOutlined fontSize="small" />,
  },
  {
    key: 'prepaymentResolutions',
    label: 'Review prepayment resolutions',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <KeyboardDoubleArrowRightOutlined fontSize="small" />,
  },
  {
    key: 'transactionRates',
    label: 'Approve outstanding transaction rates',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <SyncAltOutlined fontSize="small" />,
  },
  {
    key: 'companyValuations',
    label: 'Confirm company valuations',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('company', Number(count), 'companies')}`,
    icon: <ApartmentOutlined fontSize="small" />,
  },
  {
    key: 'unrealisedFxGainsAndLosses',
    label: (
      <>
        Approve unrealised FX gains and losses
        <Tooltip title="Transactions will be posted to the GL upon approval">
          <Typography component="span"> ⚡</Typography>
        </Tooltip>
      </>
    ),
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <ShowChartOutlined fontSize="small" />,
  },
];

export const optionalItems: Array<ChecklistItemDef> = [
  {
    key: 'awaitingCommitments',
    label: 'Close awaiting commitments',
    subLabelTemplate: (count) =>
      `${count} ${pluralize('commitment', Number(count))} pending close`,
    icon: <CheckCircleOutlined fontSize="small" />,
  },
  {
    key: 'transactionsPendingCall',
    label: 'Transactions pending call',
    subLabelTemplate: (amount) => `${amount} uncalled capital`,
    formatValue: (value, currency) =>
      numberToCurrencyString(value, { currency }),
    icon: <LocalAtmOutlined fontSize="small" />,
  },
];
